// packages/web/src/components/auth/LoginFormInner.tsx
// FAANG-GRADE: Shared login form component used by both SlideDownLogin modal and /login page
// Contains identical login logic and form UI - no duplication

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useLoginMutation } from '../../store/api/authApi';
import { logout, setCredentials } from '../../store/slices/authSlice';
import { getDashboardUrlForRole } from '../../utils/dashboardRouting';

import ShrinkTextField from '@/components/forms/ShrinkTextField';

const schema = yup
  .object({
    email: yup.string().email('Must be a valid email').required('Email required'),
    password: yup.string().min(8, 'Password at least 8 characters').required('Password required'),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

interface LoginFormInnerProps {
  catalogSlug?: string | null;
  isCatalogLogin?: boolean;
  showTitle?: boolean;
  showLinks?: boolean;
  onSuccess?: () => void;
  loading?: boolean;
  isLoadingProp?: boolean; // For SlideDownLogin compatibility
}

export function LoginFormInner({
  catalogSlug = null,
  isCatalogLogin = false,
  showTitle = true,
  showLinks = true,
  onSuccess,
  loading: externalLoading,
  isLoadingProp,
}: LoginFormInnerProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [loginMutation, { isLoading: rtkLoading }] = useLoginMutation();
  const [localError, setLocalError] = useState<string | null>(null);

  // Use external loading if provided (for SlideDownLogin), otherwise use RTK loading
  const isLoading = externalLoading !== undefined ? externalLoading : rtkLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const extractToken = (result: any) =>
    result?.tokens?.accessToken ??
    result?.token ??
    result?.accessToken ??
    result?.jwt ??
    result?.data?.token ??
    null;

  const completeLogin = async (result: any) => {
    console.log('[LoginFormInner] ===== LOGIN COMPLETION START =====');
    console.log('[LoginFormInner] Login result received:', {
      hasToken: !!result?.token,
      hasUser: !!result?.user,
      userRole: result?.user?.role,
      userId: result?.user?.id,
    });

    const token = extractToken(result);
    console.log('[LoginFormInner] Token extracted:', {
      tokenPresent: !!token,
      tokenLength: token?.length,
    });

    if (!token) {
      throw new Error('Login failed: No token received');
    }

    console.log('[LoginFormInner] User data from response:', {
      role: result.user?.role,
      email: result.user?.email,
      id: result.user?.id,
      tenantId: result.user?.tenantId,
    });

    console.log('[LoginFormInner] Dispatching setCredentials to Redux');
    const credentialsPayload = {
      token,
      user: result.user,
    };
    dispatch(setCredentials(credentialsPayload));

    console.log('[LoginFormInner] Token persisted via setCredentials');

    console.log('[LoginFormInner] Checking Redux state after dispatch');
    setTimeout(() => {
      const currentState = (window as any).__REDUX_STORE__?.getState?.()?.auth;
      console.log('[LoginFormInner] Redux state after dispatch:', {
        tokenPresent: !!currentState?.token,
        userPresent: !!currentState?.user,
        userRole: currentState?.user?.role,
      });
    }, 100);

    console.log('[LoginFormInner] Navigating based on user role...');
    const userRole = result.user?.role;
    let redirectPath = getDashboardUrlForRole(userRole);

    // For catalog-specific login, check user role
    if (isCatalogLogin) {
      if (userRole === 'customer') {
        redirectPath = `/c/${catalogSlug}`;
      } else {
        console.log(
          '[LoginFormInner] Non-customer login on catalog page, redirecting to main login',
        );
        setLocalError(
          'Administrative accounts must log in through the main marketplace. Please use visionarynow.com/login for admin access.',
        );
        dispatch(logout());
        return;
      }
    }

    console.log(
      '[LoginFormInner] User role:',
      userRole,
      'catalogSlug:',
      catalogSlug,
      'redirecting to:',
      redirectPath,
    );

    // FAANG-GRADE: Check if payment is required before allowing dashboard access
    if (result.user?.requiresPayment) {
      console.log('[LoginFormInner] PAYMENT REQUIRED - Redirecting to payment page', {
        partnerId: result.user.tenantId,
        subscriptionId: result.user.subscriptionId,
        paymentAmount: result.user.paymentAmount,
      });

      const paymentUrl = `/partner/payment?partnerId=${result.user.tenantId}&subscriptionId=${result.user.subscriptionId}&amount=${result.user.paymentAmount || 19900}&pending=true${token ? `&authToken=${encodeURIComponent(token)}` : ''}`;
      await router.push(paymentUrl);
      console.log('[LoginFormInner] ===== LOGIN COMPLETION END (PAYMENT REQUIRED) =====');
      return;
    }

    // Ensure Redux state is updated before navigation
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Store auth in sessionStorage for payment page access
    try {
      sessionStorage.setItem(
        'auth',
        JSON.stringify({
          token,
          user: result.user,
        }),
      );
      console.log('[LoginFormInner] Auth stored in sessionStorage');
    } catch (e) {
      console.warn('[LoginFormInner] Failed to store auth in sessionStorage:', e);
    }

    // Check if user needs to re-accept updated terms
    if (result.needsTermsAcceptance) {
      console.log('[LoginFormInner] TERMS RE-ACCEPTANCE REQUIRED - Redirecting');
      await router.push('/terms-reacceptance?redirect=' + encodeURIComponent(redirectPath));
      console.log('[LoginFormInner] ===== LOGIN COMPLETION END (TERMS REQUIRED) =====');
      return;
    }

    // Use role-based redirect path
    await router.push(redirectPath);
    onSuccess?.();
    console.log('[LoginFormInner] ===== LOGIN COMPLETION END =====');
  };

  const onSubmit = async (data: FormValues) => {
    console.log('[LoginFormInner] ===== FORM SUBMIT TRIGGERED =====');
    console.log('[LoginFormInner] Login attempt for email:', data.email);
    setLocalError(null);

    try {
      // Email-based routing: never send subdomain in login requests
      const payload = { email: data.email, password: data.password };
      console.log('[LoginFormInner] Payload prepared:', {
        email: payload.email,
        passwordLength: payload.password?.length,
      });

      const before = Date.now();
      let result: any;

      try {
        console.log('[LoginFormInner] Calling RTK login mutation...');
        result = await loginMutation(payload).unwrap();
        console.log('[LoginFormInner] RTK login successful:', {
          hasToken: !!result?.token,
          hasUser: !!result?.user,
          userRole: result?.user?.role,
        });
      } catch (rtkErr: any) {
        console.log('[LoginFormInner] RTK login failed:', rtkErr);
        const status = rtkErr?.status ?? rtkErr?.originalStatus;
        const apiMsg = rtkErr?.data?.message ?? rtkErr?.error ?? rtkErr?.message ?? null;

        let msg = 'Login failed';
        if (status === 401) msg = 'Login failed: Invalid email or password';
        else if (status === 403 && /verify/i.test(String(apiMsg || '')))
          msg = 'Login failed: Email not verified — please check your inbox';
        else if (status === 429) msg = 'Login failed: Too many attempts — try again shortly';
        else if (apiMsg) msg = `Login failed: ${apiMsg}`;

        console.log('[LoginFormInner] Setting RTK error:', msg);
        setLocalError(msg);
        return;
      }

      const after = Date.now();
      console.log('[LoginFormInner] Login API response time:', after - before, 'ms');

      await completeLogin(result);
    } catch (err: any) {
      console.log('[LoginFormInner] Unexpected error in onSubmit:', err);
      const status =
        err?.status ?? err?.originalStatus ?? err?.data?.status ?? err?.response?.status;
      const apiMsg =
        err?.data?.message ??
        err?.response?.data?.message ??
        (typeof err?.error === 'string' ? err.error : null);

      let msg = 'Login failed';
      if (status === 401) msg = 'Login failed: Invalid email or password';
      else if (status === 403 && /verify/i.test(String(apiMsg || '')))
        msg = 'Login failed: Email not verified — please check your inbox';
      else if (status === 429) msg = 'Login failed: Too many attempts — try again shortly';
      else if (apiMsg) msg = `Login failed: ${apiMsg}`;

      console.log('[LoginFormInner] Setting final error:', msg);
      setLocalError(msg);
    }
  };

  // Auto-submit for harness query params (only for standalone /login page)
  useEffect(() => {
    if (typeof window === 'undefined' || isCatalogLogin) return;
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');
    const password = params.get('password');

    if (email && password) {
      (async () => {
        try {
          const result = await loginMutation({ email, password }).unwrap();
          await completeLogin(result);
        } catch {
          setLocalError('Login failed: Invalid email or password');
        }
      })();
    }
  }, [isCatalogLogin, loginMutation]);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(onSubmit)(e);
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {showTitle && (
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {catalogSlug ? `Login to ${catalogSlug}` : 'Sign in'}
        </Typography>
      )}

      <ShrinkTextField
        id="email"
        {...register('email')}
        name="email"
        label="Email"
        type="email"
        autoComplete="email"
        autoFocus={!isCatalogLogin}
        fullWidth
        margin="normal"
        error={!!errors.email}
        helperText={errors.email?.message as string}
        disabled={isLoading}
        inputProps={{ 'data-testid': 'login-email' }}
      />

      <ShrinkTextField
        id="password"
        {...register('password')}
        name="password"
        label="Password"
        type="password"
        autoComplete="current-password"
        fullWidth
        margin="normal"
        error={!!errors.password}
        helperText={errors.password?.message as string}
        disabled={isLoading}
        inputProps={{ 'data-testid': 'login-password' }}
      />

      {localError && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="login-error" role="alert">
          <strong>Login failed</strong>
          {typeof localError === 'string'
            ? localError.toLowerCase().startsWith('login failed')
              ? ''
              : `: ${localError}`
            : null}
        </Alert>
      )}

      <Button
        id="login-submit"
        type="submit"
        variant="contained"
        fullWidth
        sx={{ mt: 1, py: 1.5 }}
        disabled={isLoading}
        data-testid="login-submit"
        aria-label="Login"
        onClick={() => console.log('[LoginFormInner] Button clicked')}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Login'}
      </Button>

      {showLinks && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => router.push('/forgot-password')}
          >
            Forgot password?
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={() => router.push(catalogSlug ? `/c/${catalogSlug}/register` : '/register')}
          >
            Don't have an account? Register
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default LoginFormInner;
